// ============================================
// TYPES DE BASE
// ============================================

export enum UserRole {
  ROLE_CLIENT = 'ROLE_CLIENT',
  ROLE_PROVIDER = 'ROLE_PROVIDER',
  ROLE_MODERATOR = 'ROLE_MODERATOR',
  ROLE_ADMIN = 'ROLE_ADMIN',
  ROLE_SUPPORT = 'ROLE_SUPPORT'
}

export enum JobStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ACCEPTED = 'ACCEPTED',
  IN_PROGRESS = 'IN_PROGRESS',
  DONE = 'DONE',
  CLOSED = 'CLOSED',
  CANCELLED = 'CANCELLED'
}

export enum QuoteStatus {
  SENT = 'SENT',
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED', // Was REJECTED
  WITHDRAWN = 'WITHDRAWN' // Kept for now if client-side logic uses it
}

export enum MessageStatus {
  SENT = 'SENT',
  DELIVERED = 'DELIVERED',
  READ = 'READ'
}

// ============================================
// USER & AUTH
// ============================================

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  city?: string;
  avatarUrl?: string;
  roles: UserRole[];
  displayName?: string;
  isProvider: boolean;
  emailVerified: boolean; // Added for email verification status
  hasPassword?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  user: User;
  expiresIn: number;
}

export interface GoogleRegistration {
  registrationToken: string;
  email: string;
  firstName: string;
  lastName?: string;
  avatarUrl?: string;
}

export interface CompleteGoogleRegistrationRequest {
  registrationToken: string;
  firstName: string;
  lastName?: string;
  phone: string;
  termsAccepted: boolean;
}

export interface LoginRequest {
  emailOrPhone: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  phone?: string;
  avatarUrl?: string;
  languages?: string[];
  city?: string;
  district?: string;
}

// ============================================
// CATEGORIES
// ============================================

export interface Category {
  id: string;
  slug: string;
  names: Record<string, string>;
  descriptions: Record<string, string>;
  iconUrl: string;
  parentId?: string;
  subcategories?: Category[];
  active?: boolean;
}

// ============================================
// PROVIDER PROFILE
// ============================================

export interface PortfolioItem {
  id: string;
  photoUrl: string;
  title?: string;
  description?: string;
  tags?: string[];
  displayOrder?: number;
  createdAt?: string;
}

export interface ProviderProfile {
  id: string;
  userId: string;
  businessName?: string;
  displayName?: string;
  avatarUrl?: string;
  bio?: string;
  yearsOfExperience?: number;
  hourlyRate?: number;
  categories: Category[];
  portfolio?: PortfolioItem[];
  portfolioPreview?: string[];
  serviceArea: number;
  serviceRadiusKm?: number;
  location?: {
    latitude?: number;
    longitude?: number;
    address?: string;
    cityId?: string;
    city?: string;
    regionId?: string;
    region?: string;
  };
  latitude?: number;
  longitude?: number;
  available: boolean;
  distanceKm?: number;
  averageRating: number;
  totalReviews: number;
  totalJobs: number;
  priceHint?: { value: number; label?: string };
  verifiedAt?: string;
  verified?: boolean;
  emailVerified?: boolean; // Added for Trust Block
  certified?: boolean;
  certifications?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ProviderProfileRequest {
  businessName?: string;
  bio?: string;
  yearsOfExperience?: number;
  hourlyRate?: number;
  categoryIds: string[];
  serviceArea?: number;      // Frontend alias
  serviceRadiusKm?: number;  // Backend field name
  latitude?: number;
  longitude?: number;
  address?: string;
  cityId?: string;
  available?: boolean;
}

// ============================================
// JOBS
// ============================================

export interface JobAddress {
  latitude: number;
  longitude: number;
  line1: string;
  line2?: string;
  city: string;
  postalCode?: string;
}

export interface JobBudget {
  type: 'FIXED' | 'HOURLY';
  value: number;
}

export interface JobTimeWindow {
  preferredStartDate?: string;
  preferredEndDate?: string;
  asap: boolean;
}

export interface Job {
  id: string;
  client: User; // UserDto maps to User interface roughly? Backend sends UserDto. Frontend User has id, email...
  category: Category;
  title: string;
  description: string;
  photos: string[];
  address: JobAddress;
  budget: JobBudget;
  timeWindow: JobTimeWindow;
  status: JobStatus;
  publishedAt?: string;
  acceptedAt?: string;
  completedAt?: string;
  acceptedProvider?: ProviderProfile;
  quotesCount: number;
  distanceKm?: number;
  createdAt: string;
  updatedAt: string;

  // Legacy fields for compatibility during refactor if needed (but better to remove)
  // categoryId: string; 
  // categoryName?: string;
  // city?: string; // Now in address.city
  // estimatedBudget?: number; // Now in budget.value
}

export interface CreateJobRequest {
  title: string;
  description: string;
  categoryId: string;
  latitude?: number;
  longitude?: number;
  address?: string;
  city?: string;
  budgetType?: 'FIXED' | 'HOURLY';
  budgetValue?: number;
  estimatedBudget?: number; // kept for legacy
  photoUrls?: string[];
}

// ============================================
// QUOTES
// ============================================

export interface Quote {
  id: string;
  jobId: string;
  jobTitle?: string;
  provider?: ProviderProfile; // Backend returns ProviderProfileDto
  // providerId: string; // Legacy
  // providerName?: string; // Legacy
  // providerRating?: number; // Legacy
  status: QuoteStatus;
  price: number;
  durationHours: number;
  message?: string;
  validUntil?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateQuoteRequest {
  jobId: string;
  price: number;
  durationHours: number;
  message?: string;
  validUntil?: string;
}

// ============================================
// CONVERSATIONS & MESSAGES
// ============================================

export interface Conversation {
  id: string;
  jobId?: string;
  jobTitle?: string;
  /** Set by the backend when the other participant deleted their account (writing is refused). */
  recipientDeleted?: boolean;
  client: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
  provider: {
    id: string; // User ID — NOT the provider profile ID
    firstName: string;
    lastName: string;
    avatarUrl?: string; // businessName?
    displayName?: string; // Backend UserDto doesn't have businessName usually, ProviderProfile does. UserDto has firstName/lastName.
  };
  /** Provider profile ID (ProviderProfile.id). Use this — not provider.id — for /providers/:id navigation. */
  providerProfileId?: string;
  // Aliases for backward compatibility with UI components
  clientId?: string;
  clientName?: string;
  providerName?: string;
  lastMessagePreview?: string;
  lastMessage?: string; // alias for lastMessagePreview
  lastMessageAt?: string;
  unreadCount: number;
  createdAt: string;
}

export interface Message {
  id: string;
  conversationId: string;
  sender: {
    id: string;
    firstName: string;
    lastName?: string;
  };
  text: string;
  attachments?: string[];
  isRead?: boolean;
  createdAt: string;
  readAt?: string;
}

export interface SendMessageRequest {
  conversationId: string;
  content: string;
}

// ============================================
// REVIEWS
// ============================================

export interface Review {
  id: string;
  jobId: string;
  jobTitle?: string;
  clientId: string;
  clientName?: string;
  reviewerName?: string; // Mapped from backend
  providerId: string;
  providerName?: string;
  rating: number;
  comment?: string;
  response?: string;
  createdAt: string;
}

export interface CreateReviewRequest {
  jobId: string;
  providerId?: string; // Optional as backend infers it
  rating: number;
  comment?: string;
}

// ============================================
// API RESPONSE WRAPPER
// ============================================

export interface ApiResponse<T> {
  data: T;
  message?: string;
  timestamp: string;
}

export interface ApiError {
  code: string;
  message: string;
  fields?: Record<string, string>;
  timestamp: string;
}

/** Shape of errors thrown by the axios service layer, once narrowed from `unknown` in a catch block. */
export type CaughtApiError = Partial<ApiError> & {
  status?: number;
  response?: { data?: { message?: string } };
};

// ============================================
// PAGINATION
// ============================================

export interface PageRequest {
  page?: number;
  size?: number;
  sort?: string;
}

export interface PageResponse<T> {
  items: T[];
  totalElements: number;
  total: number; // Alias for totalElements
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

